import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/layout/MarketingNav";
import MarketingFooter from "@/components/layout/MarketingFooter";
import AccessCodeSection from "@/components/marketing/AccessCodeSection";
import { MARKETING } from "@/constants/testIds";
import { launchCheckout } from "@/lib/billing";
import {
  ArrowUpRight, BarChart3, Brain, Building2, CheckCircle2, Compass, DollarSign,
  FileSpreadsheet, GraduationCap, HandCoins, Landmark, Layers, LineChart, MapPin,
  Presentation, Rocket, Scale, Sparkles, Target, Trophy, Users2, Zap,
} from "lucide-react";

const ORG_TYPES = [
  { icon: Landmark, label: "Chambers of Commerce" },
  { icon: Building2, label: "Economic Development Orgs" },
  { icon: Users2, label: "Business Associations" },
  { icon: GraduationCap, label: "Universities & Colleges" },
  { icon: Rocket, label: "Incubators & Accelerators" },
  { icon: HandCoins, label: "Foundations" },
  { icon: MapPin, label: "Community Development" },
  { icon: Target, label: "Workforce Development" },
  { icon: Scale, label: "Minority Business Orgs" },
  { icon: Sparkles, label: "Women's Business Centers" },
  { icon: Compass, label: "Government Agencies" },
];

const OUTCOMES = [
  { title: "Increase Business Survival", body: "Personalized AI coaching and continuous guidance help entrepreneurs build stronger, healthier businesses." },
  { title: "Help Raise More Capital", body: "Prepare businesses for investors, lenders, grants, procurement, and crowdfunding with AI-powered readiness." },
  { title: "Produce Better Business Plans", body: "Investor-ready plans, financial projections, and marketing strategies from guided interviews." },
  { title: "Create More Jobs", body: "Support growth through stronger planning, improved execution, and scalable coaching." },
  { title: "Improve Business Health Scores™", body: "Track readiness across marketing, finance, ops, leadership, AI adoption, and capital." },
  { title: "Improve Pitch Competition Outcomes", body: "Automated scoring, judging, coaching, and entrepreneur preparation for competitions." },
  { title: "Deliver Programs Faster", body: "Reduce manual coaching, paperwork, and reporting with intelligent automation." },
  { title: "Simplify Grant Reporting", body: "Auto-generate dashboards and reports to demonstrate impact to boards and funders." },
  { title: "Clearly Demonstrate ROI", body: "Show exactly how programs are improving businesses, jobs, capital, and local economies." },
];

const CAPABILITIES = [
  { icon: Brain, title: "Business DNA™ Assessment", body: "11-section onboarding builds a rich profile every agent uses." },
  { icon: LineChart, title: "Business Health Score™", body: "15-dimension composite score with radar chart and 90-day plan." },
  { icon: FileSpreadsheet, title: "AI Business Plan Builder", body: "Investor, bank, grant, lean, and executive summary formats." },
  { icon: CheckCircle2, title: "AI Business Auditor", body: "SWOT, risk matrix, and improvement roadmap." },
  { icon: Target, title: "Marketing Strategy Generator", body: "12-month plan with channels, calendars, and campaigns." },
  { icon: DollarSign, title: "Financial Modeling", body: "3, 5, 7, and 10-year proforma with sensitivity analysis." },
  { icon: Sparkles, title: "Business Einstein™", body: "Persistent AI advisor with memory of every document, score, and conversation." },
  { icon: BarChart3, title: "Industry Intelligence", body: "PESTLE, Porter's Five Forces, competitors, and trends." },
  { icon: Presentation, title: "Pitch Builder", body: "Deck outlines, speaker notes, and 10 elevator angles." },
  { icon: Layers, title: "Business Assets Library", body: "Every document and asset, versioned and exportable to Word & PDF." },
  { icon: HandCoins, title: "Capital Readiness", body: "Scorecard, funding path, grant matches, and investor checklist." },
  { icon: Users2, title: "AI Coach™", body: "24/7 coaching that scales your team infinitely." },
  { icon: Building2, title: "Organization Dashboard", body: "Impact KPIs, entrepreneur roster, and program-wide analytics." },
  { icon: Trophy, title: "Judge Dashboard", body: "AI summaries, scorecards, and live leaderboards for competitions." },
  { icon: Compass, title: "Coach Dashboard", body: "Real-time visibility into who needs intervention and mentoring." },
  { icon: Zap, title: "Impact Reporting", body: "Grant-ready reports for funders and stakeholders in minutes." },
  { icon: Landmark, title: "White-Label Portal", body: "Your brand. Your domain. Your program identity. Zero rebuild." },
];

const KPIS = [
  "Businesses enrolled", "Businesses launched", "Business survival rates", "Capital raised",
  "Jobs created", "Health Score improvements", "Pitch score improvements", "Industries served",
  "Women-owned businesses", "Minority-owned businesses", "Veteran-owned businesses", "Revenue growth",
  "Funding secured", "Coach effectiveness", "AI adoption", "Program completion rates",
];

const PRICING_TIERS = [
  { name: "Starter", seats: "Up to 50", price: "$8,750", perSeat: "$175", note: "For small chambers & community programs.", pkg: "org_starter" },
  { name: "Growth", seats: "Up to 100", price: "$12,500", perSeat: "$125", note: "For accelerators & regional programs.", featured: true, pkg: "org_growth" },
  { name: "Regional", seats: "Up to 250", price: "$21,250", perSeat: "$85", note: "For universities & EDOs.", pkg: "org_regional" },
  { name: "Enterprise", seats: "Up to 1,000", price: "$50,000", perSeat: "$50", note: "For statewide initiatives & major foundations.", pkg: "org_enterprise" },
  { name: "Statewide / National", seats: "1,000+", price: "Custom", perSeat: "From $45", note: "Negotiated pricing for national programs.", pkg: null },
];

const ADDONS = [
  { name: "AI Capital Raising System™", price: "$4,995", body: "Comprehensive fundraising and capital readiness platform.", pkg: "addon_capital_raising" },
  { name: "AI Pitch Competition Suite", price: "$5,000/yr", body: "Automate applications, scoring, judging, and management.", pkg: "addon_pitch_suite" },
  { name: "AI Judge Assistant", price: "$3,000/yr", body: "AI-generated summaries, scorecards, and recommendations for judges.", pkg: "addon_judge_assistant" },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-stone-50">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-stone-200 bg-stone-50">
        <div className="atlas-grid-bg atlas-radial-fade absolute inset-0 -z-10" />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 lg:px-8 lg:pt-24">
          <div className="max-w-4xl">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white/60 px-3 py-1 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="label-mono !text-[10px]">The AI Operating System for Entrepreneurial Ecosystems</span>
            </div>
            <h1 className="font-display text-6xl font-bold leading-[0.95] tracking-tight text-slate-950 sm:text-7xl lg:text-[92px]">
              ATLAS<span className="text-slate-400 align-top text-3xl lg:text-5xl">™</span>
            </h1>
            <p className="mt-3 font-display text-3xl font-medium text-stone-700 sm:text-4xl">
              The IXL Business Pro
            </p>
            <p className="mt-10 max-w-2xl text-lg leading-relaxed text-stone-700 sm:text-xl">
              Transform entrepreneurs into stronger businesses.
              <br />
              Transform programs into measurable economic impact.
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600">
              Empower every entrepreneur with an AI-powered business advisor while giving your organization the tools
              to measure outcomes, demonstrate impact, and scale programs like never before.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/request-demo" data-testid={MARKETING.heroRequestDemo} className="btn-primary">
                Request a Demo <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/request-demo?type=discovery" data-testid={MARKETING.heroDiscovery} className="btn-secondary">
                Schedule a Discovery Call
              </Link>
            </div>
          </div>

          {/* Signals row */}
          <div className="mt-24 grid grid-cols-2 gap-8 border-t border-stone-200 pt-10 sm:grid-cols-4">
            {[
              ["17", "Core AI Modules"],
              ["15", "Health Dimensions"],
              ["11", "Onboarding Sections"],
              ["∞", "Business Einstein Memory"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="kpi-num">{n}</div>
                <div className="label-mono mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACCESS CODE — complimentary access */}
      <AccessCodeSection />


      {/* TRUSTED FOR */}
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <div className="label-mono mb-6">Trusted For — Designed For</div>          <h2 className="max-w-3xl font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
            Built for every organization shaping entrepreneurial ecosystems.
          </h2>
          <div className="mt-14 flex flex-wrap gap-3">
            {ORG_TYPES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-4 py-2 text-sm text-stone-700 transition-colors hover:border-slate-900 hover:bg-white hover:text-slate-950">
                <Icon className="h-4 w-4" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section id="outcomes" className="border-b border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="label-mono mb-4">Outcomes Organizations Care About</div>
              <h2 className="font-display text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl">
                Deliver results that matter — not just documents.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-stone-600">
                ATLAS™ was built to help organizations create measurable economic impact.
                Every capability maps to an outcome your funders and stakeholders track.
              </p>
            </div>
            <div className="grid gap-4 md:col-span-7 md:grid-cols-2">
              {OUTCOMES.map((o, i) => (
                <div key={o.title} className="card-flat">
                  <div className="label-mono !text-[10px]">Outcome {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-3 font-display text-xl font-medium text-slate-950">{o.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{o.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="product" className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-4">Everything Your Organization Needs</div>
            <h2 className="font-display text-5xl font-bold tracking-tight text-slate-950 sm:text-6xl">
              One Platform.<br />
              Every Entrepreneur.<br />
              <span className="text-stone-400">Every Outcome.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600">
              ATLAS™ combines AI coaching, workflow automation, program management, and impact reporting into a single
              operating system designed for entrepreneurial ecosystems.
            </p>
          </div>
          <div className="grid gap-px overflow-hidden rounded-lg border border-stone-200 bg-stone-200 md:grid-cols-3 lg:grid-cols-4">
            {CAPABILITIES.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="group flex flex-col justify-between bg-white p-6 transition-colors hover:bg-stone-50">
                <Icon className="h-6 w-6 text-slate-900" strokeWidth={1.5} />
                <div className="mt-8">
                  <div className="label-mono !text-[10px] mb-1">Module {String(i + 1).padStart(2, "0")}</div>
                  <div className="font-display text-lg font-medium leading-tight text-slate-950">{title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM MANAGER */}
      <section className="border-b border-stone-200 bg-slate-950 text-stone-100">
        <div className="atlas-grid-bg-dark atlas-radial-fade absolute inset-x-0 h-[500px] -z-0" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="grid gap-16 md:grid-cols-2 md:items-end">
            <div>
              <div className="label-mono !text-stone-500 mb-4">Designed for Program Managers</div>
              <h2 className="font-display text-5xl font-bold tracking-tight sm:text-6xl">
                Spend less time managing.<br />
                <span className="text-stone-400">Spend more time creating impact.</span>
              </h2>
            </div>
            <ul className="grid gap-3">
              {[
                "Monitor entrepreneur progress in real time",
                "Identify businesses needing intervention",
                "Track coaching engagement",
                "Measure funding readiness",
                "Evaluate competition performance",
                "Generate executive reports in minutes",
                "Demonstrate measurable outcomes to funders",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 border-l border-stone-800 pl-4 text-stone-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" strokeWidth={2} />
                  <span className="text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ANALYTICS */}
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-4">Powerful Analytics</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              Turn program data into meaningful stories.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Monitor 16+ KPIs across your entire entrepreneurial ecosystem — from businesses launched
              to jobs created, capital raised, and revenue growth.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-stone-200 bg-stone-200 sm:grid-cols-3 md:grid-cols-4">
            {KPIS.map((k, i) => (
              <div key={k} className="bg-white p-5">
                <div className="font-mono text-[10px] text-stone-400">KPI {String(i + 1).padStart(2, "0")}</div>
                <div className="mt-2 text-sm font-medium text-slate-950">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <div className="label-mono mb-4">Pricing</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              Annual license. Priced per seat. Built for scale.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Whether you're supporting 25 entrepreneurs or 25,000, ATLAS™ scales with you.
              Implementation & onboarding is a one-time $5,000.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-5">
            {PRICING_TIERS.map((t) => (
              <div key={t.name} className={`flex flex-col rounded-lg border p-6 transition-all ${t.featured ? "border-slate-950 bg-white shadow-[0_20px_60px_-15px_rgb(15,23,42,0.2)]" : "border-stone-200 bg-white"}`}>
                {t.featured && <div className="label-mono !text-[9px] !text-emerald-600 mb-2">Most Popular</div>}
                <div className="font-display text-xl font-medium text-slate-950">{t.name}</div>
                <div className="mt-1 text-xs text-stone-500">{t.seats} entrepreneurs</div>
                <div className="mt-6 font-display text-3xl font-bold text-slate-950">{t.price}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500">per year</div>
                <div className="mt-4 rounded-md bg-stone-100 p-2 text-center font-mono text-xs text-slate-900">
                  {t.perSeat}/seat
                </div>
                <p className="mt-4 flex-1 text-xs leading-relaxed text-stone-600">{t.note}</p>
                {t.pkg ? (
                  <button
                    onClick={() => launchCheckout(t.pkg)}
                    data-testid={`pricing-checkout-${t.pkg}`}
                    className={`${t.featured ? "btn-primary" : "btn-secondary"} mt-6 !text-xs !px-4 !py-2`}
                  >
                    Purchase
                  </button>
                ) : (
                  <Link to="/request-demo?type=pricing" data-testid="pricing-cta-custom" className="btn-secondary mt-6 !text-xs !px-4 !py-2">
                    Request Pricing
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Individual */}
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-stone-200 bg-white p-6 md:col-span-1">
              <div className="label-mono mb-2">Individual Plan</div>
              <div className="font-display text-2xl font-medium text-slate-950">Entrepreneur</div>
              <div className="mt-4 font-display text-4xl font-bold text-slate-950">$195<span className="text-lg font-normal text-stone-500">/year</span></div>
              <p className="mt-3 text-xs leading-relaxed text-stone-600">Full platform access for solo founders.</p>
              <button onClick={() => launchCheckout("entrepreneur_annual")} data-testid={MARKETING.pricingCtaIndividual} className="btn-secondary mt-6 !text-xs w-full">
                Start Now — $195
              </button>
            </div>
            <div className="rounded-lg border border-stone-200 bg-white p-6 md:col-span-2">
              <div className="label-mono mb-2">Optional Program Enhancements</div>
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {ADDONS.map((a) => (
                  <div key={a.name} className="flex flex-col border-l border-stone-200 pl-4">
                    <div className="text-sm font-medium text-slate-950">{a.name}</div>
                    <div className="mt-1 font-mono text-xs text-emerald-600">{a.price}</div>
                    <p className="mt-2 text-xs leading-relaxed text-stone-600 flex-1">{a.body}</p>
                    <button onClick={() => launchCheckout(a.pkg)} data-testid={`addon-checkout-${a.pkg}`} className="btn-secondary mt-3 !text-[11px] !px-3 !py-1.5">
                      Add
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="max-w-4xl">
            <div className="label-mono mb-4">Why Organizations Choose ATLAS™</div>
            <h2 className="font-display text-4xl font-bold leading-tight tracking-tight text-slate-950 sm:text-5xl">
              Organizations don't invest in AI because it's innovative.
              <br />
              <span className="text-stone-400">They invest because it delivers measurable outcomes.</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {[
              "Strengthen entrepreneurship programs",
              "Support more businesses without adding staff",
              "Improve entrepreneur success rates",
              "Demonstrate measurable economic impact",
              "Win and retain grant funding",
              "Deliver consistent coaching at scale",
              "Create lasting value for entrepreneurs and communities",
            ].map((t) => (
              <div key={t} className="flex items-start gap-4 border-t border-stone-200 pt-6">
                <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-emerald-600" strokeWidth={1.5} />
                <span className="text-base text-slate-950">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-b border-stone-200 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
          <div className="grid gap-10 md:grid-cols-2 md:items-end">
            <div>
              <div className="label-mono !text-stone-500 mb-4">Ready to Build the Future of Entrepreneurship?</div>
              <h2 className="font-display text-5xl font-bold leading-tight tracking-tight sm:text-6xl">
                Whether you're supporting 25 entrepreneurs or 25,000, ATLAS™ gives your organization the technology, intelligence, and insights to help every business reach its full potential.
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/request-demo" data-testid={MARKETING.ctaFooter} className="btn-primary !bg-white !text-slate-950 hover:!bg-stone-100">
                Schedule a Personalized Demo <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link to="/request-demo?type=pricing" className="btn-secondary !border-stone-700 !bg-transparent !text-white hover:!bg-white/5">
                Request Pricing
              </Link>
              <Link to="/request-demo?type=discovery" className="btn-secondary !border-stone-700 !bg-transparent !text-white hover:!bg-white/5">
                Talk to Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
